import { useState, useEffect, useRef, useReducer } from 'react'
import { i18n } from '@fs/zion-locale'
import { getDynamicLinks } from './getDynamicLinks'
import useDynamicNavigationFlagContext from './useDynamicNavigationFlagContext'

function stringsToBoolean(value) {
  if (typeof value === 'object' && !Array.isArray(value)) {
    return Object.keys(value).reduce((obj, key) => ({ ...obj, [key]: stringsToBoolean(value[key]) }), {})
  }
  if (Array.isArray(value)) {
    return value.map(stringsToBoolean)
  }
  return value === 'true' || value === 'false' ? value === 'true' : value
}

function flagOverrideToString(flagOverrides) {
  if (!flagOverrides) return ''
  return Object.entries(flagOverrides)
    .map(([key, value]) => `${value.treatment === 'on' ? '' : '!'}${key}`)
    .join(',')
}

function reducer(state, action) {
  const { type, navigation, flags } = action
  switch (type) {
    case 'SET_NAVIGATION':
      return {
        ...state,
        isLoading: false,
        isError: false,
        navigation,
      }
    case 'SET_NAVIGATION_ERROR':
      return {
        ...state,
        isLoading: false,
        isError: true,
        initiateFetch: false,
      }
    case 'FETCHING_NAVIGATION':
      return {
        ...state,
        isLoading: true,
        isError: false,
        initiateFetch: false,
      }
    case 'REFETCH_NAVIGATION':
      return {
        ...state,
        isLoading: true,
        isError: false,
        initiateFetch: true,
        flags: flags ?? state.flags,
      }
    default:
      return state
  }
}

export default function useDynamicNavigation() {
  const flagOverrides = useDynamicNavigationFlagContext()
  const [state, dispatch] = useReducer(reducer, { isLoading: true, isError: false, initiateFetch: true, flags: null })
  const [queryParams, setQueryParams] = useState({})
  const mounted = useRef(true)

  // Function to update query params and trigger a refresh
  const setDynamicQueryParams = (newParams) => {
    setQueryParams(newParams)
    dispatch({ type: 'REFETCH_NAVIGATION' })
  }

  // If language changes we need to refresh the navigation - needs to run on every render to catch changes
  const [language, setLanguage] = useState(i18n.language)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (language !== i18n.language) {
      setLanguage(i18n.language)
      dispatch({ type: 'REFETCH_NAVIGATION' })
    }
  })

  useEffect(() => {
    mounted.current = true
    if (state.flags === null || flagOverrideToString(flagOverrides) !== state.flags) {
      dispatch({ type: 'REFETCH_NAVIGATION', flags: flagOverrideToString(flagOverrides) })
    } else if (state.initiateFetch) {
      dispatch({ type: 'FETCHING_NAVIGATION' })
      const queryParamString = new URLSearchParams(queryParams).toString()
      const allqueryParams = `${queryParamString}${queryParamString && state.flags ? '&' : ''}${state.flags ? `forceFeatureFlags=${state.flags}` : ''}`
      getDynamicLinks(allqueryParams)
        .then((newNavigation) => {
          if (mounted.current) {
            dispatch({ type: 'SET_NAVIGATION', navigation: stringsToBoolean(newNavigation) })
          }
        })
        .catch(() => {
          if (mounted.current) {
            dispatch({ type: 'SET_NAVIGATION_ERROR' })
          }
        })
    }
    return () => {
      mounted.current = false
    }
  }, [state.isLoading, state.initiateFetch, state.flags, flagOverrides, queryParams])

  return { ...state, setDynamicQueryParams }
}
